/**
 * Native "open file" bridge (ElectronVisualized for Mac, Atomizer for Mac).
 *
 * The Mac apps register molecule document types, so Finder "Open With",
 * a drop on the Dock icon and File > Open all hand a file to the app.
 * The app reads it and calls:
 *
 *     window.__nativeOpenFile({ name: "caffeine.xyz", text: "..." })
 *
 * or, if the bundle hasn't loaded yet, pushes the same object onto
 * `window.__EV_PENDING_FILES`, which `installNativeFileOpen` drains.
 * The file is routed to the Workbench, which loads it through the same
 * `loadFile` path as its own file input and drag-and-drop.
 */

export interface NativeFile {
  name: string;
  text: string;
}

export const NATIVE_FILE_EVENT = "ev:native-open-file";

let pending: NativeFile | null = null;

declare global {
  interface Window {
    __nativeOpenFile?: (f: NativeFile) => boolean;
    __EV_PENDING_FILES?: NativeFile[];
  }
}

/** Install the bridge. `openWorkbench` navigates to the Workbench route. */
export function installNativeFileOpen(openWorkbench: () => void): void {
  const accept = (f: NativeFile): boolean => {
    if (!f || typeof f.name !== "string" || typeof f.text !== "string") return false;
    pending = { name: f.name, text: f.text };
    openWorkbench();
    // An already-mounted Workbench picks it up from the event; a
    // freshly mounted one takes it on mount.
    window.dispatchEvent(new Event(NATIVE_FILE_EVENT));
    return true;
  };
  window.__nativeOpenFile = accept;
  const queued = window.__EV_PENDING_FILES;
  window.__EV_PENDING_FILES = [];
  if (Array.isArray(queued) && queued.length) accept(queued[queued.length - 1]);
}

/** Take (and clear) the file waiting for the Workbench, if any. */
export function takePendingNativeFile(): NativeFile | null {
  const f = pending;
  pending = null;
  return f;
}
